/**
 * ================================================================
 * API SERVICE: Reports Service
 * ================================================================
 *
 * @service          reportsApi
 * @description      API service for managing all report data
 * @baseUrl          reportsBaseUrl
 *
 * @endpoints
 * - GET    /holdings-report - Fetch holdings report data
 * - GET    /ledger-report - Fetch ledger report data
 * - GET    /position-report - Fetch position report data
 * - GET    /contract-report - Fetch contract report data
 * - GET    /global-summary-report - Fetch global summary report data
 * - GET    /download-holdings - Download holdings CSV/Excel
 * - GET    /download-ledger - Download ledger CSV/Excel
 * - GET    /download-position - Download position CSV/Excel
 * - GET    /download-contract-file - Download individual contract file
 * - GET    /download-global - Download global summary CSV/Excel
 *
 * @authentication  Bearer token required for all endpoints
 * @rateLimit        100 requests per minute per user
 *
 * @caching
 * - Strategy: RTK Query automatic caching with tags
 * - Tags: ['HoldingsReport', 'LedgerReport', 'PositionReport', 'ContractReport', 'GlobalReport']
 * - TTL: 2 minutes default, no caching for downloads
 *
 * @errorHandling
 * - Network errors: Automatic retry with exponential backoff
 * - Auth errors: Redirect to login page
 * - Validation errors: Display user-friendly messages
 *
 * @types
 * - All report types imported from @/types/reports
 * - Download center types imported from @/types/downloads
 * - Standardized error response format from @/types/common
 *
 * ================================================================
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer } from '@/lib/api/baseQuery';
import { prepareAuthHeaders } from '@/lib/session/sessionManager';
import type { APIResponse } from '@/types/api';
import type {
  ContractApiResponse,
  ContractFileDownloadResponse,
  ContractQueryParams,
  DownloadReportResponse,
  ExpenseDetailRequest,
  ExpenseDetailResponse,
  GlobalApiResponse,
  GlobalQueryParams,
  HoldingsApiResponse,
  HoldingsQueryParams,
  LedgerApiResponse,
  LedgerQueryParams,
  PositionApiResponse,
  PositionQueryParams,
  ScripDetailRequest,
  ScripDetailResponse,
  ScripSummaryRequest,
  ScripSummaryResponse,
} from '@/types/reports';

const reportsBaseUrl = import.meta.env.VITE_REPORTS_URL;
// const drillDownUrl = `${import.meta.env.VITE_DRILL_DOWN_URL}/v2/service/reports`;

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: reportsBaseUrl,
    // ================================================================
    // BASE QUERY CONFIGURATION
    // ================================================================
    //
    // Authentication: Include AuthToken in headers via common utility
    // Error handling: Transform errors to standard format
    // Request/Response: JSON content type with proper serialization
    // ================================================================
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ['HoldingsReport', 'LedgerReport', 'PositionReport', 'ContractReport', 'GlobalReport'],
  endpoints: (builder) => ({
    // ================================================================
    // HOLDINGS REPORT ENDPOINTS
    // ================================================================

    /**
     * Fetch holdings report data with optional filtering
     *
     * @endpoint GET /trading-holding-report
     * @param {HoldingsQueryParams} params - Query parameters and filters
     * @returns {HoldingsApiResponse} Holdings data with summary
     * @cache Provides 'HoldingsReport' tag for invalidation
     */
    getHoldingsReport: builder.query<HoldingsApiResponse, HoldingsQueryParams>({
      query: (params) => ({
        url: 'trading-holding-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      providesTags: ['HoldingsReport'],
      keepUnusedDataFor: 120, // Cache data for 2 minutes
      transformErrorResponse: createApiErrorTransformer('HoldingsReport', true),
    }),

    /**
     * Download holdings report as CSV/Excel
     *
     * @endpoint GET /trading-holding-report
     * @param {HoldingsQueryParams} params - Query parameters for download
     * @returns {DownloadReportResponse} File blob for download
     * @cache No caching for downloads
     */
    downloadHoldingsReport: builder.query<DownloadReportResponse, HoldingsQueryParams>({
      query: (params) => ({
        url: 'trading-holding-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      keepUnusedDataFor: 0, // Disable caching for downloads
      transformErrorResponse: createApiErrorTransformer('HoldingsReportDownload', false),
    }),

    // ================================================================
    // LEDGER REPORT ENDPOINTS
    // ================================================================

    /**
     * Fetch ledger report data with date range and filters
     *
     * @endpoint GET /ledger-report
     * @param {LedgerQueryParams} params - Query parameters and filters
     * @returns {LedgerApiResponse} Ledger data with summary
     * @cache Provides 'LedgerReport' tag for invalidation
     */
    getLedgerReport: builder.query<LedgerApiResponse, LedgerQueryParams>({
      query: (params) => ({
        url: 'ledger-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      providesTags: ['LedgerReport'],
      keepUnusedDataFor: 120, // Cache data for 2 minutes
      transformErrorResponse: createApiErrorTransformer('LedgerReport', false),
    }),

    getScripSummary: builder.mutation<ScripSummaryResponse, ScripSummaryRequest>({
      query: ({ clientCode, ...body }) => ({
        url: `/scrip-summary`,
        method: 'POST',
        body,
        params: {
          source: 'ap',
          clientCode,
        },
      }),
      transformErrorResponse: createApiErrorTransformer('ReportsScripSummary', true),
    }),
    getScripDetail: builder.mutation<ScripDetailResponse, ScripDetailRequest>({
      query: (data) => ({
        url: `/scrip-detail`,
        method: 'POST',
        body: data,
        params: {
          source: 'ap',
          clientCode: data.clientId,
        },
      }),
      transformErrorResponse: createApiErrorTransformer('ReportsScripDetail', true),
    }),
    getExpenseDetails: builder.mutation<ExpenseDetailResponse, ExpenseDetailRequest>({
      query: (data) => ({
        url: `/expense-details`,
        method: 'POST',
        body: data,
        params: {
          source: 'ap',
          clientCode: data.clientId,
        },
      }),
      transformErrorResponse: createApiErrorTransformer('ReportsExpenseDetails', true),
    }),

    /**
     * Download ledger report as Excel
     *
     * @endpoint GET /ledger-report
     * @param {LedgerQueryParams} params - Query parameters for download
     * @returns {DownloadReportResponse} File blob for download
     * @cache No caching for downloads
     */
    downloadLedgerReport: builder.query<DownloadReportResponse, LedgerQueryParams>({
      query: (params) => ({
        url: 'ledger-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('LedgerReportDownload', false),
    }),

    // ================================================================
    // POSITION REPORT ENDPOINTS
    // ================================================================

    /**
     * Fetch position report data with segment filtering
     *
     * @endpoint GET /position-report
     * @param {PositionQueryParams} params - Query parameters and filters
     * @returns {PositionApiResponse} Position data with summary
     * @cache Provides 'PositionReport' tag for invalidation
     */
    getPositionReport: builder.query<PositionApiResponse, PositionQueryParams>({
      query: (params) => ({
        url: 'position-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      providesTags: ['PositionReport'],
      keepUnusedDataFor: 120,
      transformErrorResponse: createApiErrorTransformer('PositionReport', false),
    }),

    /**
     * Download position report as CSV/Excel
     *
     * @endpoint GET /position-report
     * @param {PositionQueryParams} params - Query parameters for download
     * @returns {DownloadReportResponse} File blob for download
     * @cache No caching for downloads
     */
    downloadPositionReport: builder.query<DownloadReportResponse, PositionQueryParams>({
      query: (params) => ({
        url: 'position-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('PositionReportDownload', false),
    }),

    // ================================================================
    // CONTRACT REPORT ENDPOINTS
    // ================================================================

    /**
     * Fetch contract report data with date range filtering
     *
     * @endpoint GET /contract-report
     * @param {ContractQueryParams} params - Query parameters and filters
     * @returns {ContractApiResponse} Contract data
     * @cache Provides 'ContractReport' tag for invalidation
     */
    getContractReport: builder.query<ContractApiResponse, ContractQueryParams>({
      query: (params) => ({
        url: 'contract-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      providesTags: ['ContractReport'],
      keepUnusedDataFor: 120,
      transformErrorResponse: createApiErrorTransformer('ContractReport', false),
    }),

    /**
     * Download individual contract file
     *
     * @endpoint GET /download-contract-file
     * @param {object} params - Contract file parameters
     * @returns {DownloadReportResponse} PDF file blob for download
     * @cache No caching for downloads
     */
    downloadContractFile: builder.query<
      APIResponse<ContractFileDownloadResponse>,
      { docFilename: string; docDate: string; clientCode: string }
    >({
      query: ({ docFilename, docDate, clientCode }) => ({
        url: 'get-file',
        params: {
          docFilename,
          docDate,
          source: 'ap',
          clientCode,
        },
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('ContractFileDownload', false),
    }),

    // ================================================================
    // GLOBAL SUMMARY REPORT ENDPOINTS
    // ================================================================

    /**
     * Fetch global summary report data with comprehensive filtering
     *
     * @endpoint GET /global-summary-report
     * @param {GlobalQueryParams} params - Query parameters and filters
     * @returns {GlobalApiResponse} Global summary data
     * @cache Provides 'GlobalReport' tag for invalidation
     */
    getGlobalSummaryReport: builder.query<GlobalApiResponse, GlobalQueryParams>({
      query: (params) => ({
        url: 'global-summary-report',
        params: {
          ...params,
          source: 'ap',
        },
      }),
      providesTags: ['GlobalReport'],
      keepUnusedDataFor: 120,
      transformErrorResponse: createApiErrorTransformer('GlobalSummaryReport', false),
    }),

    /**
     * Download global summary report as CSV/Excel
     *
     * @endpoint GET /global-summary-report
     * @param {GlobalQueryParams} params - Query parameters for download
     * @returns {DownloadReportResponse} File blob for download
     * @cache No caching for downloads
     */
    downloadGlobalSummaryReport: builder.query<DownloadReportResponse, GlobalQueryParams>({
      query: (params) => ({
        url: 'global-summary-report',
        params: {
          ...params,
          source: 'ap',
          format: 'csv',
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const filename =
            response.headers.get('Content-Disposition')?.split('filename=')[1] ||
            'global-summary-report.csv';
          return { data: blob, filename };
        },
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('GlobalSummaryReportDownload', false),
    }),
  }),
});

// ================================================================
// EXPORTED HOOKS
// ================================================================
//
// Auto-generated hooks for use in React components
// Naming convention: use[EndpointName][Query|Mutation]
// ================================================================

export const {
  useGetHoldingsReportQuery,
  useLazyGetHoldingsReportQuery,
  useLazyDownloadHoldingsReportQuery,
  useGetLedgerReportQuery,
  useLazyGetLedgerReportQuery,
  useGetScripSummaryMutation,
  useGetScripDetailMutation,
  useGetExpenseDetailsMutation,
  useLazyDownloadLedgerReportQuery,
  useGetPositionReportQuery,
  useLazyGetPositionReportQuery,
  useLazyDownloadPositionReportQuery,
  useGetContractReportQuery,
  useLazyGetContractReportQuery,
  useLazyDownloadContractFileQuery,
  useGetGlobalSummaryReportQuery,
  useLazyGetGlobalSummaryReportQuery,
  useLazyDownloadGlobalSummaryReportQuery,
} = reportsApi;

/**
 * ================================================================
 * END OF SERVICE: Reports Service
 * ================================================================
 *
 * @exports          reportsApi, hooks for React components
 * @dependencies     RTK Query, TypeScript types from @/types/reports
 * @usedBy           Report components and pages (non-download center)
 *
 * @maintenance
 * - Cache strategy: Review TTL settings quarterly
 * - Error handling: Monitor error rates and adjust retry logic
 * - Performance: Track query performance and optimize slow endpoints
 *
 * @separation
 * - Download center functionality moved to @/services/downloadCenterService
 * - This service now focuses solely on report viewing and direct downloads
 * - Improved separation of concerns and code organization
 *
 * ================================================================
 */
